import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErroreApi, chiama } from '../api/cliente.js';
import {
  type DocumentoCompleto,
  useCreaDocumento,
  useOpzioniDocumenti,
  useSalvaDocumento,
} from '../api/documenti.js';
import { Modale } from '../componenti/Modale.js';
import { t } from '../i18n/lingua.js';
import { usePuoAprire } from '../permessi.js';

/**
 * Il modulo di un documento: gli stessi campi per crearlo e per modificarlo.
 *
 * Il file non si carica qui. Un documento nuovo nasce con i suoi dati e poi,
 * dalla sua scheda, si trascina dentro il file — perché il caricamento vuole
 * vedere cosa succede (quanto pesa, come si chiamerà, chi altro usa quel
 * file) e in una finestra che si chiude quelle cose non si vedono.
 *
 * Cliente e ordine si cercano: sono milleduecento e cinquantamila.
 */
export function ModaleDocumento({
  documento,
  onChiudi,
  ordineId,
  ordineNumero,
}: {
  /** Nullo per un documento nuovo. */
  documento: DocumentoCompleto | null;
  onChiudi: () => void;
  /** Quando si crea dalla scheda di un ordine, l'ordine è già deciso. */
  ordineId?: number;
  ordineNumero?: string;
}) {
  const navigate = useNavigate();
  const puo = usePuoAprire();
  const crea = useCreaDocumento();
  const salva = useSalvaDocumento(documento?.id ?? 0);
  const opzioni = useOpzioniDocumenti();

  const [cerca, setCerca] = useState('');
  const [cliente, setCliente] = useState<{ id: number; name: string } | null>(
    documento?.identityId ? { id: documento.identityId, name: documento.customer ?? '' } : null,
  );
  const [campi, setCampi] = useState({
    title: documento?.title ?? '',
    protocolNumber: documento?.protocolNumber ?? '',
    description: documento?.description ?? '',
    keywords: documento?.keywords ?? '',
    notes: documento?.notes ?? '',
  });
  const [categorie, setCategorie] = useState<number[]>(
    documento?.categories.map((c) => c.id) ?? [],
  );

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const clienti = useQuery({
    queryKey: ['clienti-per-documento', cerca],
    queryFn: () =>
      chiama<{ rows: { id: number; name: string; bcCodeSpa: string | null }[] }>(
        `/api/identities?type=customer&size=15&q=${encodeURIComponent(cerca)}`,
      ),
    enabled: puo('customers') && cerca.trim().length >= 2,
  });

  const inCorso = crea.isPending || salva.isPending;
  const errore =
    crea.error instanceof ErroreApi
      ? crea.error.message
      : salva.error instanceof ErroreApi
        ? salva.error.message
        : null;

  const scegli = (id: number) =>
    setCategorie((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const conferma = () => {
    const corpo = {
      title: campi.title.trim(),
      protocolNumber: campi.protocolNumber.trim() || null,
      description: campi.description.trim() || null,
      keywords: campi.keywords.trim() || null,
      notes: campi.notes.trim() || null,
      identityId: cliente?.id ?? null,
      categoryIds: categorie,
      ...(documento ? {} : { orderId: ordineId ?? null }),
    };

    if (documento) {
      salva.mutate(corpo, { onSuccess: onChiudi });
    } else {
      crea.mutate(corpo, {
        onSuccess: ({ id }) => {
          onChiudi();
          // si entra subito nella scheda: il passo dopo è caricarci il file
          void navigate({ to: '/documents/$id', params: { id: String(id) } });
        },
      });
    }
  };

  const tutte = [...(opzioni.data?.categories ?? []), ...(opzioni.data?.categories2 ?? [])];

  return (
    <Modale
      titolo={documento ? t('Modifica il documento') : 'Nuovo documento'}
      occhiello={documento ? documento.title : ordineNumero ? `PO# ${ordineNumero}` : undefined}
      nota={
        documento
          ? undefined
          : t('Il file si carica dopo, dalla scheda del documento: lì si vede quanto pesa e con che nome esce.')
      }
      larga
      inCorso={inCorso}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={inCorso}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={campi.title.trim() === '' || inCorso}
          >
            {inCorso ? 'Salvo…' : documento ? 'Salva' : t('Crea il documento')}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="fields">
        <div className="field span-2">
          <label htmlFor="do-titolo">
            {t('Titolo')}<span className="req">*</span>
            <span className="dim" style={{ fontWeight: 400 }}>
              {' '}
              {t('— è anche il nome con cui esce il file, quando quello vero non dice niente')}
            </span>
          </label>
          <input
            id="do-titolo"
            className="input"
            value={campi.title}
            autoFocus
            onChange={(e) => cambia('title', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="do-protocollo">{t('Numero di protocollo')}</label>
          <input
            id="do-protocollo"
            className="input"
            value={campi.protocolNumber}
            onChange={(e) => cambia('protocolNumber', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="do-parole">{t('Parole chiave')}</label>
          <input
            id="do-parole"
            className="input"
            placeholder={t('separate da virgola')}
            value={campi.keywords}
            onChange={(e) => cambia('keywords', e.target.value)}
          />
        </div>
      </div>

      <div className="field span-2" style={{ margin: '14px 0' }}>
        <label htmlFor="do-cliente">{t('Cliente')}</label>
        {!puo('customers') ? (
          // la qualità vede i documenti ma non i clienti: quello che c'è resta
          <div className="scelto">
            {cliente ? <strong>{cliente.name}</strong> : <span className="dim">—</span>}
          </div>
        ) : cliente ? (
          <div className="scelto">
            <strong>{cliente.name}</strong>
            <button className="btn btn-ghost" onClick={() => setCliente(null)}>
              {t('Togli')}
            </button>
          </div>
        ) : (
          <>
            <input
              id="do-cliente"
              className="input"
              placeholder={t('Scrivi almeno due lettere')}
              value={cerca}
              onChange={(e) => setCerca(e.target.value)}
            />
            {(clienti.data?.rows ?? []).length > 0 && (
              <ul className="risultati">
                {clienti.data!.rows.map((c) => (
                  <li key={c.id}>
                    <button
                      className="risultato"
                      onClick={() => {
                        setCliente({ id: c.id, name: c.name });
                        setCerca('');
                      }}
                    >
                      <span>{c.name}</span>
                      {c.bcCodeSpa && <span className="dim num">{c.bcCodeSpa}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {/* le categorie sono ventidue in tutto: pastiglie, non due tendine —
          si vede subito quali sono attive e se ne aggiunge una con un clic */}
      <div className="field span-2" style={{ marginBottom: 14 }}>
        <label>{t('Categorie')}</label>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {tutte.map((c) => (
            <button
              key={c.id}
              className={categorie.includes(c.id) ? 'chip chip-set' : 'chip'}
              onClick={() => scegli(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="fields">
        <div className="field span-2">
          <label htmlFor="do-descrizione">{t('Descrizione')}</label>
          <textarea
            id="do-descrizione"
            className="input"
            rows={2}
            value={campi.description}
            onChange={(e) => cambia('description', e.target.value)}
          />
        </div>
        <div className="field span-2">
          <label htmlFor="do-note">{t('Annotazioni')}</label>
          <textarea
            id="do-note"
            className="input"
            rows={2}
            value={campi.notes}
            onChange={(e) => cambia('notes', e.target.value)}
          />
        </div>
      </div>
    </Modale>
  );
}
